@file:OptIn(androidx.compose.ui.ExperimentalComposeUiApi::class)

package com.trevorwiebe.timesheet.settlements.presentation

import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Check
import androidx.compose.material3.AssistChip
import androidx.compose.material3.Card
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.backhandler.BackHandler
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.LoadSelectionSummaryCard
import com.trevorwiebe.timesheet.core.presentation.common.NativeDatePicker
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveWidth
import com.trevorwiebe.timesheet.core.presentation.common.TimeSheetButton
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.presentation.admin.EditLoadDialog
import com.trevorwiebe.timesheet.settlements.domain.settlementName

@Composable
internal fun NewSettlementContent(state: SettlementsState, onEvent: (SettlementsEvent) -> Unit) {
    val form = state.newSettlement ?: return
    val loads = state.settleableLoads
    val blocker = state.newSettlementBlocker
    BackHandler { onEvent(SettlementsEvent.OnDismissNewSettlement) }
    var rateLoad by remember { mutableStateOf<Load?>(null) }

    Scaffold(
        topBar = {
            TopBar(
                title = "New Settlement",
                backIcon = { BackIcon() },
                onBack = { onEvent(SettlementsEvent.OnDismissNewSettlement) },
            )
        },
        bottomBar = {
            ResponsiveWidth {
                Column(Modifier.fillMaxWidth().navigationBarsPadding().padding(12.dp)) {
                    form.error?.let {
                        Text(it, color = MaterialTheme.colorScheme.error, fontSize = 13.sp)
                    }
                    TimeSheetButton(
                        onClick = { onEvent(SettlementsEvent.OnCreateSettlement) },
                        text = "Create draft settlement",
                        modifier = Modifier.fillMaxWidth(),
                        loading = form.isSaving,
                        enabled = state.canCreateSettlement,
                    )
                }
            }
        },
    ) { innerPadding ->
        ResponsiveContent(modifier = Modifier.padding(innerPadding)) {
            Column(Modifier.fillMaxSize()) {
                Column(Modifier.padding(horizontal = 12.dp, vertical = 8.dp)) {
                    PayeePicker(state = state, onEvent = onEvent)
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        AssistChip(
                            onClick = { onEvent(SettlementsEvent.OnStartDateClick) },
                            label = { Text("From: " + (form.startDate?.let { Util.toFriendlyDate(it) } ?: "Any date")) },
                        )
                        AssistChip(
                            onClick = { onEvent(SettlementsEvent.OnEndDateClick) },
                            label = { Text("To: " + Util.toFriendlyDate(form.endDate)) },
                        )
                        if (form.startDate != null) {
                            TextButton(onClick = { onEvent(SettlementsEvent.OnStartDateSelected(null)) }) {
                                Text("All dates", color = MaterialTheme.colorScheme.tertiary)
                            }
                        }
                    }
                    state.newSettlementBasePayLabel?.let {
                        Text(
                            text = it,
                            fontSize = 13.sp,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                        )
                    }
                }

                when {
                    form.ownerOperatorId.isEmpty() && form.carrierId.isEmpty() ->
                        Message("Choose an owner operator or carrier to see their unsettled loads.")
                    blocker != null -> Message(blocker)
                    loads.isEmpty() -> Message(
                        if (form.carrierId.isNotEmpty()) "No unsettled loads for this carrier in this date range."
                        else "No unsettled loads for these drivers in this date range."
                    )
                    else -> {
                        LoadSelectionSummaryCard(
                            summary = state.newSettlementSummary,
                            notice = state.unpayableNotice,
                            onSelectAll = { onEvent(SettlementsEvent.OnSelectAllLoads) },
                            onClear = { onEvent(SettlementsEvent.OnClearLoadSelection) },
                        )
                        LazyColumn(Modifier.fillMaxSize()) {
                            items(loads, key = { it.load.id }) { item ->
                                SettleableLoadRow(
                                    item = item,
                                    checked = item.load.id in form.selectedLoadIds,
                                    onToggle = { onEvent(SettlementsEvent.OnToggleLoad(item.load.id)) },
                                    onOpen = { rateLoad = item.load },
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    rateLoad?.let { EditLoadDialog(it, onDismiss = { rateLoad = null }) }
    NativeDatePicker(
        date = form.startDate ?: form.endDate,
        showDatePicker = form.showStartPicker,
        onDismiss = { onEvent(SettlementsEvent.OnDismissDatePickers) },
        onDateSelected = { onEvent(SettlementsEvent.OnStartDateSelected(it)) },
    )
    NativeDatePicker(
        date = form.endDate,
        showDatePicker = form.showEndPicker,
        onDismiss = { onEvent(SettlementsEvent.OnDismissDatePickers) },
        onDateSelected = { onEvent(SettlementsEvent.OnEndDateSelected(it)) },
    )
}

@Composable
private fun Message(text: String) {
    Box(Modifier.fillMaxSize()) {
        Text(
            text = text,
            textAlign = TextAlign.Center,
            modifier = Modifier.align(Alignment.Center).padding(24.dp),
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
        )
    }
}

@Composable
private fun PayeePicker(state: SettlementsState, onEvent: (SettlementsEvent) -> Unit) {
    var showDialog by remember { mutableStateOf(false) }
    val form = state.newSettlement ?: return
    val selectedName = state.newSettlementPayeeName
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(bottom = 8.dp)
            .clip(RoundedCornerShape(4.dp))
            .border(1.dp, MaterialTheme.colorScheme.primary, RoundedCornerShape(4.dp))
            .clickable { showDialog = true }
            .padding(horizontal = 16.dp, vertical = 14.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = selectedName ?: "Choose who to pay",
            color = if (selectedName == null) MaterialTheme.colorScheme.onSurfaceVariant
            else MaterialTheme.colorScheme.onBackground,
            modifier = Modifier.weight(1f),
        )
        Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = null)
    }
    if (showDialog) {
        val ownerOperatorCounts = state.outstandingByOwnerOperator
        val carrierCounts = state.outstandingByCarrier
        Dialog(onDismissRequest = { showDialog = false }) {
            Card(modifier = Modifier.responsiveDialogWidth(), shape = RoundedCornerShape(16.dp)) {
                Column(Modifier.padding(top = 20.dp, bottom = 8.dp)) {
                    Text(
                        text = "Choose who to pay",
                        fontSize = 20.sp,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier.padding(horizontal = 24.dp),
                    )
                    Spacer(Modifier.height(12.dp))
                    HorizontalDivider()
                    LazyColumn(Modifier.heightIn(max = 440.dp)) {
                        item { PayeeSectionHeader("Owner operators") }
                        items(state.activeOwnerOperators, key = { "o-" + it.id }) { ownerOperator ->
                            PayeeRow(
                                name = ownerOperator.settlementName(),
                                contactName = ownerOperator.contactName,
                                count = ownerOperatorCounts[ownerOperator.id] ?: 0,
                                selected = ownerOperator.id == form.ownerOperatorId,
                                onClick = {
                                    showDialog = false
                                    onEvent(SettlementsEvent.OnSelectOwnerOperator(ownerOperator.id))
                                },
                            )
                        }
                        if (state.activeCarriers.isNotEmpty()) {
                            item { PayeeSectionHeader("Carriers") }
                        }
                        items(state.activeCarriers, key = { "c-" + it.id }) { carrier ->
                            PayeeRow(
                                name = carrier.settlementName(),
                                contactName = carrier.contactName,
                                count = carrierCounts[carrier.id] ?: 0,
                                selected = carrier.id == form.carrierId,
                                onClick = {
                                    showDialog = false
                                    onEvent(SettlementsEvent.OnSelectCarrier(carrier.id))
                                },
                            )
                        }
                    }
                    HorizontalDivider()
                    Row(Modifier.fillMaxWidth().padding(horizontal = 12.dp), horizontalArrangement = Arrangement.End) {
                        TextButton(onClick = { showDialog = false }) {
                            Text("Cancel", color = MaterialTheme.colorScheme.tertiary)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun PayeeSectionHeader(title: String) {
    Text(
        text = title.uppercase(),
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
        modifier = Modifier.padding(start = 24.dp, end = 24.dp, top = 12.dp, bottom = 4.dp),
    )
}

@Composable
private fun PayeeRow(name: String, contactName: String, count: Int, selected: Boolean, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(horizontal = 24.dp, vertical = 14.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(Modifier.weight(1f)) {
            Text(
                text = name,
                fontSize = 16.sp,
                fontWeight = if (selected) FontWeight.SemiBold else null,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            if (contactName.isNotBlank() && contactName != name) {
                Text(
                    text = contactName,
                    fontSize = 13.sp,
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                )
            }
        }
        if (count > 0) {
            Text(
                text = "$count unsettled",
                fontSize = 13.sp,
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                modifier = Modifier.padding(start = 12.dp),
            )
        }
        if (selected) {
            Icon(
                imageVector = Icons.Default.Check,
                contentDescription = "Selected",
                tint = MaterialTheme.colorScheme.tertiary,
                modifier = Modifier.padding(start = 8.dp).size(20.dp),
            )
        }
    }
}

/** The checkbox picks the load for the settlement; the rest of the row opens the load to edit it. */
@Composable
private fun SettleableLoadRow(item: SettleableLoad, checked: Boolean, onToggle: () -> Unit, onOpen: () -> Unit) {
    val line = item.line
    val muted = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onOpen)
            .padding(end = 12.dp, top = 2.dp, bottom = 2.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Checkbox(
            checked = checked,
            onCheckedChange = { onToggle() },
            enabled = item.isPayable,
            colors = CheckboxDefaults.colors(
                checkedColor = MaterialTheme.colorScheme.tertiary,
                checkmarkColor = Color.White,
            ),
        )
        Column(Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = Util.toFriendlyDate(line.date) + if (line.loadNumber.isNotBlank()) "  #${line.loadNumber}" else "",
                    fontSize = 14.sp,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f),
                )
                if (item.isPayable) {
                    Text(text = "Pay ${line.payFormatted}", fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
                } else {
                    Text(text = "Needs a rate", fontSize = 13.sp, color = MaterialTheme.colorScheme.error)
                }
            }
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = "${line.driverName} • ${line.pickup}  to  ${line.drop}",
                    fontSize = 13.sp,
                    color = muted,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f),
                )
                if (item.isPayable) {
                    Text(
                        text = "of ${line.grossFormatted}",
                        fontSize = 13.sp,
                        color = muted,
                        modifier = Modifier.padding(start = 8.dp),
                    )
                }
            }
        }
    }
    HorizontalDivider()
}
